/**
 * TODO 5.1 — Barra de Progresso do Welcome Wizard
 * TODO D.2/D.3 — Aplicar tokens de tema e aumentar contraste (Dev 05)
 * 
 * Renderiza um LinearProgress com transição suave, exibindo a fórmula de desaceleração.
 * Mostra "Quase lá! 🙌" quando progresso >= 80%.
 * Presente em TODAS as telas de pergunta (Tela 1 até o Expediente).
 * Pulada na Tela 0 (boas-vindas) e Telas 9/10 (processando/resumo) forçam 100%.
 */

import { Box, LinearProgress, Typography } from '@mui/material';
import { calculateProgress, getProgressMotivation } from './welcomeProgress';

/**
 * @component WelcomeProgressBar
 * 
 * @param {number} stepIndex - Índice do passo (base 1). Para Tela 0, passe null ou omita para pular.
 * @param {boolean} [force100] - Se true, força 100% na barra (Telas 9/10).
 * @param {object} wizard - OBRIGATÓRIO: wizard do useWelcomeWizard para acessar cores do tema.
 *                           Evita acoplamento com hook e duplicação de estado.
 * 
 * Uso:
 *   <WelcomeProgressBar stepIndex={1} wizard={wizard} />         ← 20%
 *   <WelcomeProgressBar stepIndex={5} wizard={wizard} />         ← 67%
 *   <WelcomeProgressBar force100 wizard={wizard} />              ← 100%
 */
export const WelcomeProgressBar = ({ stepIndex = null, force100 = false, wizard }) => {
  if (!wizard) {
    console.warn('WelcomeProgressBar: prop "wizard" é obrigatório');
    return null;
  }
  // Se stepIndex for null ou undefined, a barra não aparece (Tela 0)
  if (stepIndex === null || stepIndex === undefined) {
    return null;
  }

  // Calcula progresso
  const progress = force100 ? 100 : calculateProgress(stepIndex);
  const motivation = getProgressMotivation(progress);

  // Cores do tema para aplicar (Achado #2 — temático, Achado #3 — melhorar contraste)
  const themeColors = wizard.template?.colors || {};
  const primaryColor = themeColors.primary || '#1976D2';
  const primaryDark = themeColors.primaryDark || '#115293';
  const backgroundColor = themeColors.backgroundPaper || '#f5f5f5';
  const dividerColor = themeColors.divider || 'rgba(0, 0, 0, 0.12)';
  
  // Achado #3: Usar text.primary em vez de success para maior contraste na motivação
  // success em barbershop/beauty-salon/clinic fica em ~2.78:1, muito baixo
  // text.primary oferece melhor contraste e ainda respeita o tema
  const motivationColor = themeColors.text || '#000';

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: backgroundColor,
        padding: '16px 24px',
        borderBottom: `1px solid ${dividerColor}`,
      }}
    >
      {/* Barra com transição suave — usa primary do tema */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          transition: 'all 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: dividerColor,
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
          },
        }}
      />

      {/* Texto de motivação — só ≥80%, com melhor contraste (Achado #3) */}
      {motivation && (
        <Typography
          variant="body2"
          sx={{
            marginTop: '12px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 500,
            color: motivationColor,
            animation: 'fadeIn 0.5s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          {motivation}
        </Typography>
      )}
    </Box>
  );
};

export default WelcomeProgressBar;
